/**
 * @ Author: Housefolios
 * @ Create Time: 2025-07-10 12:35:27
 * @ Modified by: David Helmick
 * @ Modified time: 2025-07-11 17:45:28
 * @ Description: Main component that orchestrates PDF generation using modular components
 */

/**
 * Refactored Property PDF Component
 * Main component that orchestrates PDF generation using modular components
 */

import React, { useEffect, useState, Fragment } from 'react'
import { useMutation } from '@apollo/client/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { enqueueSnackbar } from 'notistack'
import {
  IconButton,
  ListItemButton,
  Button,
  CircularProgress,
  Box,
} from '@mui/material'
import { Close } from '@mui/icons-material'

import withSession from '@/housefolios-components/Session/withSession'
import { GET_PROPERTY } from '../queries'
import { SEND_EMAIL } from '../mutations'
import useAffiliate from '@/hooks/useAffiliate'
import { usePrevious } from '@/hooks/usePrevious'
import { isEqual, startCase } from '@/utils/lodash'

import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from '@/vfs_fontes'

// Import refactored components
import { usePDFState } from './hooks/usePDFState'
import { PDFGeneratorFactory } from './generators/PDFGeneratorFactory'
import EmailDialog from './components/EmailDialog'
import MobilePDFViewer from './components/MobilePDFViewer'
import {
  getBase64ImageFromUrl,
  createPdfOutputPromise,
  sanitizePdfDocDefinition,
} from './utils/pdfUtils'
import { isMobileDevice } from './utils/deviceUtils'

// pdfmake 0.3+: register VFS via addVirtualFileSystem (not pdfMake.vfs = …)
pdfMake.addVirtualFileSystem(pdfFonts.default)

const PropertyPDFRefactored = (props) => {
  const { me } = props.session
  const [{ affiliate, urlAffiliate, urlDomain }] = useAffiliate({})

  // Use custom hook for state management
  const {
    state,
    updateState,
    toggleEmailModal,
    handleChange,
    setEmailImagePage,
    setFlyers,
    setBackdrop,
    clearEmails,
    setDataURL,
    setBlob,
  } = usePDFState(props)

  const prevProps = usePrevious(props)
  const prevState = usePrevious(state)

  // GraphQL mutations
  const [sendEmail] = useMutation(SEND_EMAIL)

  // Generate PDF function
  const generatePDF = async (docDefinition) => {
    return pdfMake.createPdf(sanitizePdfDocDefinition(docDefinition))
  }

  // Create PDF document
  const createDoc = async (property) => {
    try {
      setBackdrop(true)

      if (
        property.proformaFile &&
        (props.uploaded || state.type === 'uploaded')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property.proformaFile.url,
        )
        setDataURL(dataURL[0])
        setBackdrop(false)
      } else if (
        property?.realeflowReportURL &&
        (props.CMA || state.type === 'CMA')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property?.realeflowReportURL,
        )
        setDataURL(dataURL[0])
        setBackdrop(false)
      } else {
        const docDefinition = await PDFGeneratorFactory.generatePDF(
          state.type,
          property,
          state,
          props.closeSnackbar,
          urlAffiliate,
          affiliate,
          urlDomain,
          props.session,
          props.comps || [],
        )

        const pdf = await generatePDF(docDefinition)
        const dataURL = await createPdfOutputPromise(pdf, 'dataUrl')
        setDataURL(dataURL)
        setBackdrop(false)
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      setBackdrop(false)
      enqueueSnackbar('Error generating PDF. Please try again.', {
        variant: 'error',
        action: (key) => (
          <Fragment>
            <IconButton
              onClick={() => {
                props.closeSnackbar(key)
              }}
              size="large"
            >
              <Close style={{ color: 'white' }} />
            </IconButton>
          </Fragment>
        ),
      })
    }
  }

  // Create PDF document for display
  const createDoc2 = async (property) => {
    try {
      setBackdrop(true)

      if (
        property.proformaFile &&
        (props.uploaded || state.type === 'uploaded')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property.proformaFile.url,
        )
        if (!dataURL?.[1]) {
          throw new Error('Failed to load uploaded proforma file')
        }
        setBlob(URL.createObjectURL(dataURL[1]))
        setBackdrop(false)
      } else if (
        property?.realeflowReportURL &&
        (props.CMA || state.type === 'CMA')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property?.realeflowReportURL,
        )
        if (!dataURL?.[1]) {
          throw new Error('Failed to load CMA report')
        }
        setBlob(URL.createObjectURL(dataURL[1]))
        setBackdrop(false)
      } else {
        const docDefinition = await PDFGeneratorFactory.generatePDF(
          state.type,
          property,
          state,
          props.closeSnackbar,
          urlAffiliate,
          affiliate,
          urlDomain,
          props.session,
          props.comps || [],
        )

        const pdf = await generatePDF(docDefinition)
        const blob = await createPdfOutputPromise(pdf, 'blob')
        setBlob(URL.createObjectURL(blob))
        setBackdrop(false)
      }
    } catch (error) {
      console.error('Error generating PDF for display:', error)
      setBackdrop(false)
      enqueueSnackbar('Error generating PDF. Please try again.', {
        variant: 'error',
        action: (key) => (
          <Fragment>
            <IconButton
              onClick={() => {
                props.closeSnackbar(key)
              }}
              size="large"
            >
              <Close style={{ color: 'white' }} />
            </IconButton>
          </Fragment>
        ),
      })
    }
  }

  // Download PDF
  const downloadPDF = async (property) => {
    try {
      setBackdrop(true)

      if (
        property.proformaFile &&
        (props.uploaded || state.type === 'uploaded')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property.proformaFile.url,
        )
        window.open(URL.createObjectURL(dataURL[1]), '_blank')
      } else if (
        property?.realeflowReportURL &&
        (props.CMA || state.type === 'CMA')
      ) {
        const dataURL = await getBase64ImageFromUrl(
          property?.realeflowReportURL,
        )
        window.open(URL.createObjectURL(dataURL[1]), '_blank')
      } else {
        const docDefinition = await PDFGeneratorFactory.generatePDF(
          state.type,
          property,
          state,
          props.closeSnackbar,
          urlAffiliate,
          affiliate,
          urlDomain,
          props.session,
          props.comps || [],
        )

        const pdf = await generatePDF(docDefinition)

        await Promise.resolve(
          pdf.download(
            `${property.address}-${startCase(property.city)}-${startCase(property.state)}-${property.zip}-CMA.pdf`,
          ),
        )
      }
      setBackdrop(false)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      setBackdrop(false)
      enqueueSnackbar('Error downloading PDF. Please try again.', {
        variant: 'error',
        action: (key) => (
          <Fragment>
            <IconButton
              onClick={() => {
                props.closeSnackbar(key)
              }}
              size="large"
            >
              <Close style={{ color: 'white' }} />
            </IconButton>
          </Fragment>
        ),
      })
    }
  }

  // Handle email submission
  const onSubmit = async (event, emailsOverride) => {
    setBackdrop(true)

    try {
      await sendEmail({
        variables: {
          emails: emailsOverride ?? state.emails,
          base64: state.dataURL,
          CMA: state.type === 'CMA',
          attachments:
            state.flyers?.map((flyer) => flyer.value) || [],
        },
      })

      clearEmails()
      setBackdrop(false)

      if (props.financingProforma) {
        props.updateProperty({
          refetchQueries: [
            {
              query: GET_PROPERTY,
              variables: { id: props.propertyId },
            },
          ],
        })
        props.handleClose()
      } else {
        toggleEmailModal()
      }

      enqueueSnackbar('Email Sent', {
        variant: 'success',
        action: (key) => (
          <Fragment>
            <IconButton
              onClick={() => {
                props.closeSnackbar(key)
              }}
              size="large"
            >
              <Close style={{ color: 'white' }} />
            </IconButton>
          </Fragment>
        ),
      })
    } catch (error) {
      console.error('Error sending email:', error)
      clearEmails()
      setBackdrop(false)

      if (props.financingProforma) {
        props.updateProperty({
          refetchQueries: [
            {
              query: GET_PROPERTY,
              variables: { id: props.propertyId },
            },
          ],
        })
        props.handleClose()
      } else {
        toggleEmailModal()
      }

      enqueueSnackbar('Email Failed to Send', {
        variant: 'error',
        action: (key) => (
          <Fragment>
            <IconButton
              onClick={() => {
                props.closeSnackbar(key)
              }}
              size="large"
            >
              <Close style={{ color: 'white' }} />
            </IconButton>
          </Fragment>
        ),
      })
    }
  }

  // Initial PDF generation
  useEffect(() => {
    const { property } = props
    if (state.display) {
      void createDoc2(property)
    } else {
      void createDoc(property)
    }
  }, [])

  // Handle property changes and regenerate PDF
  useEffect(() => {
    const { property } = props
    if (
      (prevState && prevState.type !== state.type) ||
      (prevProps && prevProps.marketing !== props.marketing) ||
      (prevProps && prevProps.fixFlip !== props.fixFlip) ||
      (prevProps && prevProps.wholesale !== props.wholesale) ||
      (prevProps &&
        prevProps.shortTermRental !== props.shortTermRental) ||
      ((props.uploaded || state.type === 'uploaded') &&
        !isEqual(
          prevProps?.property?.proformaFile,
          property.proformaFile,
        )) ||
      (prevState && prevState.emailImagePage !== state.emailImagePage)
    ) {
      if (state.display) {
        setBlob('')
        createDoc2(property)
      } else {
        createDoc(property)
      }
    }
  }, [
    prevState?.type,
    state.type,
    prevProps?.marketing,
    props.marketing,
    prevProps?.fixFlip,
    props.fixFlip,
    prevProps?.wholesale,
    props.wholesale,
    prevProps?.shortTermRental,
    props.shortTermRental,
    props.uploaded,
    prevProps?.property?.proformaFile,
    props.property.proformaFile,
    prevState?.emailImagePage,
    state.emailImagePage,
  ])

  // Render email dialog
  const renderEmailDialog = () => {
    if (!state.showEmail) return null

    return (
      <EmailDialog
        open={state.showEmail}
        onClose={toggleEmailModal}
        onSubmit={onSubmit}
        state={state}
        handleChange={handleChange}
        setEmailImagePage={setEmailImagePage}
        setFlyers={setFlyers}
        property={props.property}
        session={props.session}
        closeSnackbar={props.closeSnackbar}
      />
    )
  }

  const getPdfFileName = () => {
    const { property } = props
    if (!property?.address) return 'proforma.pdf'

    return `${property.address}-${startCase(property.city)}-${startCase(property.state)}-${property.zip}-proforma.pdf`
  }

  // Render PDF for display. Mobile browsers cannot scroll multi-page PDFs in iframes.
  const renderPDFDisplay = () => {
    if (!state.display || !state.blob) return null

    const blobUrl =
      typeof state.blob === 'string'
        ? state.blob
        : URL.createObjectURL(state.blob)

    if (isMobileDevice()) {
      return (
        <MobilePDFViewer
          blobUrl={blobUrl}
          fileName={getPdfFileName()}
        />
      )
    }

    return (
      <Box sx={{ height: '100%', minHeight: 0, width: '100%' }}>
        <iframe
          src={blobUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          title="PDF Viewer"
        />
      </Box>
    )
  }

  // Render download button
  const renderDownloadButton = () => {
    if (state.display || !props.download || props.editProforma)
      return null

    return (
      // <button
      //   onClick={() => downloadPDF(props.property)}
      //   disabled={state.backdrop}
      //   style={{
      //     padding: '10px 20px',
      //     backgroundColor: '#1976d2',
      //     color: 'white',
      //     border: 'none',
      //     borderRadius: '4px',
      //     cursor: state.backdrop ? 'not-allowed' : 'pointer',
      //     opacity: state.backdrop ? 0.6 : 1,
      //   }}
      // >
      //   {state.backdrop ? 'Generating...' : 'Download PDF'}
      // </button>
      <ListItemButton
        onClick={
          state.backdrop ? null : () => downloadPDF(props.property)
        }
        sx={props.download ? {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          cursor: "pointer",
          columnGap: "8px",
          "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
          pl: 3,
          color: "inherit",
          textDecoration: "none",
        } : {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          cursor: "pointer",
          columnGap: "8px",
          "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "8px",
          minWidth: 0,
          flex: "1 1 auto",
          justifyContent: "flex-start",
          "& > .MuiBox-root:first-of-type": {
            flex: "0 0 auto",
            width: 16,
            minWidth: 16,
            marginRight: 0,
          },
        }}>
          <Box sx={{
            width: 16,
            minWidth: 16,
            flexShrink: 0,
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 10,
            fontSize: "1.15rem",
            "& > svg": { width: "1em", height: "1em" },
            ".Mui-selected > &": { opacity: "1 !important" },
          }}>
            <FontAwesomeIcon icon={['far', 'file-lines']} />
          </Box>
          <span>
            {props.CMA
              ? 'CMA'
              : props.uploaded
                ? 'Uploaded File'
                : 'Proforma'}
          </span>
        </Box>
      </ListItemButton>
    )
  }

  // Render share button
  const renderShareButton = () => {
    if (state.display || props.download || props.editProforma)
      return null

    return (
      <ListItemButton
        onClick={state.backdrop ? null : toggleEmailModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          cursor: "pointer",
          columnGap: "8px",
          "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "8px",
          minWidth: 0,
          flex: "1 1 auto",
          justifyContent: "flex-start",
          "& > .MuiBox-root:first-of-type": {
            flex: "0 0 auto",
            width: 16,
            minWidth: 16,
            marginRight: 0,
          },
        }}>
          <Box sx={{
            width: 16,
            minWidth: 16,
            flexShrink: 0,
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 10,
            fontSize: "1.15rem",
            "& > svg": { width: "1em", height: "1em" },
            ".Mui-selected > &": { opacity: "1 !important" },
          }}>
            <FontAwesomeIcon icon={['far', 'envelope']} />
          </Box>
          <span>{'Email Report'}</span>
        </Box>
      </ListItemButton>
    )
  }

  // Render save button for editProforma mode
  const renderSaveButton = () => {
    if (!props.editProforma) return null

    return (
      <div
        style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={(event) => {
            // Handle back button click - this should go to previous step
            if (props.handleBackClick) {
              props.handleBackClick()
            }
          }}

          sx={{ mx: '0.5rem' }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (props.handleBackDrop) {
              props.handleBackDrop(props.updateProperty)
            }

            props
              .updateProperty({
                refetchQueries: [
                  {
                    query: GET_PROPERTY,
                    variables: { id: props.propertyId },
                  },
                ],
              })
              .then(({ data: { updateProperty } }) => {
                // Just save the changes, don't download anything
                console.log('Proforma saved successfully')
                if (props.handleClick) {
                  props.handleClick()
                }
              })
              .catch((error) => {
                console.error('Error updating property:', error)
                enqueueSnackbar(
                  'Error saving proforma. Please try again.',
                  {
                    variant: 'error',
                    autoHideDuration: 3000,
                  },
                )
              })
          }}
          style={{
            color: 'white',
          }}
          disabled={props.disabled || state.backdrop}
        >
          Save
        </Button>
      </div>
    )
  }

  return (
    <Box
      sx={
        state.display
          ? {
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }
          : undefined
      }
    >
      {state.display && state.backdrop && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {renderPDFDisplay()}
      <div style={{ textAlign: 'center' }}>
        {renderDownloadButton()}
        {renderShareButton()}
        {renderSaveButton()}
      </div>
      {renderEmailDialog()}
    </Box>
  )
}

export default withSession(PropertyPDFRefactored)
